import { useEffect, useState } from "react"
import { useMaterialUIController } from "context"

// @mui material components
import Grid from "@mui/material/Grid"
import CircularProgress from "@mui/material/CircularProgress"
import Card from "@mui/material/Card"
import Divider from "@mui/material/Divider"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import TextField from "@mui/material/TextField"
import Collapse from "@mui/material/Collapse"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import Icon from "@mui/material/Icon"

// Material Dashboard 2 React components
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDPagination from "components/MDPagination"
import MDTypography from "components/MDTypography"

// Material Dashboard 2 React examples
import DashboardLayout from "monitor/LayoutContainers/DashboardLayout"
import DashboardNavbar from "monitor/Navbars/DashboardNavbar"
import Footer from "monitor/Footer"

// Asistencia page components
import AsistenciaInformation from "layouts/asistencia/components/AsistenciaInformation"

// React Router
import { Link, useNavigate } from "react-router-dom"

// Config and JWT
import config from "config"
import { jwtDecode } from "jwt-decode"

// Services
import {
  fetchAsistentes,
  fetchDependencias,
  fetchTiposAsistente,
  fetchSucursales,
} from "layouts/asistencia/components/add/service"

function Asistencia() {
  const [controller] = useMaterialUIController()
  const { darkMode } = controller

  const token = (sessionStorage.getItem("token") || localStorage.getItem("token"))
  const navigate = useNavigate()

  // Data states
  const [asistentes, setAsistentes] = useState([])
  const [sucursales, setSucursales] = useState([])
  const [dependencias, setDependencias] = useState([])
  const [tiposAsistente, setTiposAsistente] = useState([])

  // Loading and pagination
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState(true)
  const [pageIndex, setPageIndex] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [totalElements, setTotalElements] = useState(0)

  // Filters
  const [selectedSucursal, setSelectedSucursal] = useState("")
  const [selectedOrganizacion, setSelectedOrganizacion] = useState("")
  const [selectedTipoAsistente, setSelectedTipoAsistente] = useState("")
  const [busquedaText, setBusquedaText] = useState("")

  // Verify token
  useEffect(() => {
    if (!token) {
      navigate("/authentication/sign-in", { replace: true })
      return
    }
    try {
      const { exp } = jwtDecode(token)
      const nowSec = Math.floor(Date.now() / 1000)
      if (!exp || exp < nowSec) {
        navigate("/authentication/sign-in", { replace: true })
      }
    } catch {
      navigate("/authentication/sign-in", { replace: true })
    }
  }, [token, navigate])

  // Load initial data
  useEffect(() => {
    loadSucursales()
  }, [])

  useEffect(() => {
    if (selectedSucursal) {
      loadDependencias()
    } else {
      setDependencias([])
      setSelectedOrganizacion("")
    }
  }, [selectedSucursal])

  useEffect(() => {
    if (selectedOrganizacion) {
      loadTiposAsistente()
    } else {
      setTiposAsistente([])
      setSelectedTipoAsistente("")
    }
  }, [selectedOrganizacion])

  // Load asistentes when page changes or on mount
  useEffect(() => {
    loadAsistentes(pageIndex)
  }, [pageIndex])

  const loadSucursales = async () => {
    try {
      const data = await fetchSucursales()
      setSucursales(data || [])
    } catch (error) {
      console.error("Error loading sucursales:", error)
    }
  }

  const loadDependencias = async () => {
    try {
      const data = await fetchDependencias(selectedSucursal)
      setDependencias(data || [])
    } catch (error) {
      console.error("Error loading organizaciones:", error)
    }
  }

  const loadTiposAsistente = async () => {
    try {
      const data = await fetchTiposAsistente(selectedOrganizacion)
      setTiposAsistente(data || [])
    } catch (error) {
      console.error("Error loading tipos de asistente:", error)
    }
  }

  const loadAsistentes = async (page) => {
    setLoading(true)
    try {
      const params = {
        page,
        size: 10,
      }

      if (selectedOrganizacion) {
        params.dependenciaId = selectedOrganizacion
      }

      if (selectedTipoAsistente) {
        params.tipoAsistenteId = selectedTipoAsistente
      }

      if (busquedaText) {
        params.documentNumber = busquedaText
      }

      const result = await fetchAsistentes(params)

      if (result.content) {
        setAsistentes(result.content)
        setTotalPages(result.totalPages || 0)
        setTotalElements(result.totalElements || 0)
      } else {
        setAsistentes([])
        setTotalPages(0)
        setTotalElements(0)
      }
    } catch (error) {
      console.error("Error loading asistentes:", error)
      setAsistentes([])
    } finally {
      setLoading(false)
    }
  }

  const gotoPage = (p) => {
    setPageIndex(p)
  }

  const reloadAsistentes = () => {
    loadAsistentes(pageIndex)
  }

  const handleBuscar = () => {
    setPageIndex(0)
    loadAsistentes(0)
  }

  const handleClearFilters = () => {
    setSelectedSucursal("")
    setSelectedOrganizacion("")
    setSelectedTipoAsistente("")
    setBusquedaText("")
    setPageIndex(0)
    setAsistentes([])
  }

  const handleExpandClick = () => {
    setExpanded(!expanded)
  }

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault()
      handleBuscar()
    }
  }

  const PaginationItems = () => {
    const pages = []
    let startPage = Math.max(pageIndex - 1, 0)
    let endPage = Math.min(pageIndex + 2, totalPages)

    for (let i = startPage; i < endPage; i++) {
      pages.push(i)
    }

    if (startPage > 0) {
      pages.unshift("...")
      pages.unshift(0)
    }

    if (endPage < totalPages) {
      pages.push("...")
      pages.push(totalPages - 1)
    }

    return (
      <>
        {pages.map((i, idx) =>
          i === "..." ? (
            <span key={idx} style={{ margin: "0 8px" }}>
              ...
            </span>
          ) : (
            <MDPagination item key={i} onClick={() => gotoPage(i)} active={pageIndex === i}>
              <MDTypography
                variant="caption"
                color={pageIndex === i ? "white" : darkMode ? "white" : "secondary"}
                sx={{ fontSize: 11 }}
              >
                {i + 1}
              </MDTypography>
            </MDPagination>
          )
        )}
      </>
    )
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={8}>
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
              {/* Filtros */}
              <Card>
                <MDBox px={2}>
                  <MDBox display="flex" alignItems="center" py={2} px={2}>
                    <MDTypography variant="h5" fontWeight="medium" textTransform="capitalize">
                      Filtros
                    </MDTypography>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      onClick={handleExpandClick}
                      sx={{ cursor: "pointer", ml: 1 }}
                    >
                      {expanded ? <ExpandLessIcon fontSize="medium" /> : <ExpandMoreIcon fontSize="medium" />}
                    </MDBox>
                  </MDBox>

                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Grid container spacing={2} pb={2}>
                      {/* Sucursal */}
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>Sucursal</InputLabel>
                          <Select
                            label="Sucursal"
                            variant="outlined"
                            fullWidth
                            value={selectedSucursal}
                            onChange={(e) => setSelectedSucursal(e.target.value)}
                            sx={{ p: 1.5 }}
                          >
                            <MenuItem value="">Ninguna</MenuItem>
                            {sucursales.map((suc) => (
                              <MenuItem key={suc.idSucursal} value={suc.idSucursal}>
                                {suc.descripcion}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Dependencia */}
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>Dependencia</InputLabel>
                          <Select
                            label="Dependencia"
                            variant="outlined"
                            fullWidth
                            value={selectedOrganizacion}
                            onChange={(e) => setSelectedOrganizacion(e.target.value)}
                            sx={{ p: 1.5 }}
                            disabled={!selectedSucursal}
                          >
                            <MenuItem value="">Ninguna</MenuItem>
                            {dependencias.map((org) => (
                              <MenuItem key={org.id} value={org.id}>
                                {org.nombre}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Tipo de Asistente */}
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>Tipo de Asistente</InputLabel>
                          <Select
                            label="Tipo de Asistente"
                            variant="outlined"
                            fullWidth
                            value={selectedTipoAsistente}
                            onChange={(e) => setSelectedTipoAsistente(e.target.value)}
                            sx={{ p: 1.5 }}
                            disabled={!selectedOrganizacion}
                          >
                            <MenuItem value="">Ninguno</MenuItem>
                            {tiposAsistente.map((tipo) => (
                              <MenuItem key={tipo.id} value={tipo.id}>
                                {tipo.nombre}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Búsqueda */}
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          label="Buscar por documento/nombre"
                          variant="outlined"
                          fullWidth
                          value={busquedaText}
                          onChange={(e) => setBusquedaText(e.target.value)}
                          onKeyDown={handleKeyDown}
                        />
                      </Grid>

                      {/* Botones */}
                      <Grid item xs={12}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                          <MDBox>
                            <MDButton type="submit" variant="gradient" color="info" onClick={handleBuscar}>
                              Buscar
                            </MDButton>
                            &nbsp;
                            <MDButton type="button" variant="outlined" color="info" onClick={handleClearFilters}>
                              Limpiar
                            </MDButton>
                          </MDBox>

                          <Link to="/asistencia/agregar" style={{ textDecoration: "none" }}>
                            <MDButton type="button" variant="gradient" color="success">
                              Agregar
                            </MDButton>
                          </Link>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </Collapse>
                </MDBox>
              </Card>

              <Divider />

              {/* Tabla de Asistentes */}
              <Card pt={5} mt={3}>
                {/* Paginación Superior */}
                <MDBox mt={2} pr={2}>
                  <MDPagination>
                    {totalElements > 10 && (
                      <MDTypography variant="overline" color="light" fontWeight="light" verticalAlign="middle">
                        {new Intl.NumberFormat("en-US", { minimumFractionDigits: 0 }).format(
                          10 * pageIndex + 1
                        )}{" "}
                        a{" "}
                        {new Intl.NumberFormat("en-US", { minimumFractionDigits: 0 }).format(
                          Math.min((pageIndex + 1) * 10, totalElements)
                        )}{" "}
                        de{" "}
                        {new Intl.NumberFormat("en-US", { minimumFractionDigits: 0 }).format(totalElements)}
                        &nbsp;&nbsp;
                      </MDTypography>
                    )}
                    {pageIndex > 0 && (
                      <MDPagination item onClick={() => gotoPage(pageIndex - 1)}>
                        <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                      </MDPagination>
                    )}
                    <PaginationItems />
                    {pageIndex < totalPages - 1 && (
                      <MDPagination item onClick={() => gotoPage(pageIndex + 1)}>
                        <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                      </MDPagination>
                    )}
                  </MDPagination>
                </MDBox>

                {/* Contenido */}
                {loading ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={4}>
                    <CircularProgress />
                  </MDBox>
                ) : asistentes && asistentes.length > 0 ? (
                  <AsistenciaInformation asistentes={asistentes} onReload={reloadAsistentes} />
                ) : (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={4}>
                    <MDTypography variant="h6" color="text">
                      No hay datos para mostrar
                    </MDTypography>
                  </MDBox>
                )}

                {/* Paginación Inferior */}
                <MDBox mb={2} pr={2}>
                  <MDPagination>
                    {totalElements > 10 && (
                      <MDTypography variant="overline" color="light" fontWeight="light" verticalAlign="middle">
                        {new Intl.NumberFormat("en-US", { minimumFractionDigits: 0 }).format(
                          10 * pageIndex + 1
                        )}{" "}
                        a{" "}
                        {new Intl.NumberFormat("en-US", { minimumFractionDigits: 0 }).format(
                          Math.min((pageIndex + 1) * 10, totalElements)
                        )}{" "}
                        de{" "}
                        {new Intl.NumberFormat("en-US", { minimumFractionDigits: 0 }).format(totalElements)}
                        &nbsp;&nbsp;
                      </MDTypography>
                    )}
                    {pageIndex > 0 && (
                      <MDPagination item onClick={() => gotoPage(pageIndex - 1)}>
                        <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                      </MDPagination>
                    )}
                    <PaginationItems />
                    {pageIndex < totalPages - 1 && (
                      <MDPagination item onClick={() => gotoPage(pageIndex + 1)}>
                        <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                      </MDPagination>
                    )}
                  </MDPagination>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  )
}

export default Asistencia
